/**
 * SSE Event handlers for the Cosimo chat stream.
 * Extracted from ChatContext to improve testability and separation of concerns.
 */

/**
 * Module-level cache for LangSmith fields by run_id.
 * This persists across navigation so TraceLink can look up fields
 * even after the chat history is reloaded from the server.
 */
import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { type searchMetadataStore } from "@/lib/search-metadata-store";
import {
  type CosimoAgentResponse,
  type SearchWithRecommendations,
  type ShopAgentErrorEvent,
  type ShopAgentMessageEvent,
  type ShopAgentMetadataEvent,
  type ShopAgentSearchEvent,
  parseCosimoAgentResponse,
} from "@/lib/shop-agent-client";
import { forgetStoredSearchMiss } from "@/lib/stored-search-lookup";

import {
  type BrandResult,
  type ChatMessage,
  type SearchTool,
  extractBrandResults,
  updateSearchToolsWithLabels,
} from "./chat-message-parser";

interface LangSmithFields {
  langsmithProject?: string;
  langsmithWorkspaceId?: string;
  langsmithProjectId?: string;
}

const LANGSMITH_CACHE_KEY = "langsmith_fields_cache";
const MAX_CACHE_ENTRIES = 100; // Limit cache size to prevent unbounded growth

interface LangSmithCacheEntry extends LangSmithFields {
  runId?: string;
  timestamp?: number; // For cache eviction
}

/**
 * Get cached LangSmith fields by run_id or thread_id.
 * Uses sessionStorage to persist across navigation.
 * @param runId - The run ID (preferred lookup key)
 * @param threadId - The thread ID (fallback lookup key)
 */
export function getLangSmithFieldsFromCache(
  runId?: string,
  threadId?: string
): (LangSmithFields & { runId?: string }) | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const cacheStr = sessionStorage.getItem(LANGSMITH_CACHE_KEY);
    if (!cacheStr) return undefined;
    const cache = JSON.parse(cacheStr) as Record<string, LangSmithCacheEntry>;

    // First try by run_id (most specific)
    if (runId && cache[`run:${runId}`]) {
      return cache[`run:${runId}`];
    }

    // Fall back to thread_id (for messages loaded from history without run_id)
    if (threadId && cache[`thread:${threadId}`]) {
      return cache[`thread:${threadId}`];
    }

    return undefined;
  } catch (e) {
    console.error("[LangSmithCache] Error reading cache:", e);
    return undefined;
  }
}

/**
 * Store LangSmith fields in sessionStorage by both run_id and thread_id.
 * This allows lookup even when messages are reloaded from history without run_id.
 */
export function cacheLangSmithFields(
  runId: string,
  threadId: string | undefined,
  fields: LangSmithFields
): void {
  if (typeof window === "undefined") return;
  try {
    const cacheStr = sessionStorage.getItem(LANGSMITH_CACHE_KEY);
    let cache = cacheStr ? (JSON.parse(cacheStr) as Record<string, LangSmithCacheEntry>) : {};

    const entry: LangSmithCacheEntry = { ...fields, runId, timestamp: Date.now() };

    // Store by run_id (primary key)
    cache[`run:${runId}`] = entry;

    // Also store by thread_id (fallback for history reload)
    // Note: This overwrites previous entries for the same thread, keeping the latest
    if (threadId) {
      cache[`thread:${threadId}`] = entry;
    }

    // Enforce cache size limit by removing oldest entries
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHE_ENTRIES) {
      // Sort by timestamp (oldest first) and remove excess
      entries.sort((a, b) => (a[1].timestamp || 0) - (b[1].timestamp || 0));
      const toRemove = entries.length - MAX_CACHE_ENTRIES;
      const keysToRemove = entries.slice(0, toRemove).map(([key]) => key);
      cache = Object.fromEntries(entries.slice(toRemove));
      console.debug(`[LangSmithCache] Evicted ${keysToRemove.length} old entries`);
    }

    sessionStorage.setItem(LANGSMITH_CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.error("[LangSmithCache] Error writing cache:", e);
  }
}

/** Type alias for the search metadata store */
type SearchMetadataStoreType = typeof searchMetadataStore;

/**
 * Result of handling an SSE event.
 * - 'continue': Continue processing more events
 * - 'break': Stop processing (stream ended or error)
 */
export type EventHandlerResult = "continue" | "break";

/**
 * Context provided to event handlers containing all dependencies.
 */
export interface StreamHandlerContext {
  /** Mutable array to collect search tools during streaming */
  searchTools: SearchTool[];
  /** Mutable array to collect brand results during streaming */
  brandResults: BrandResult[];
  /** Timestamp for the assistant message */
  assistantTimestamp: string;
  /** Ref to control auto-navigation behavior */
  shouldAutoNavigate: { current: boolean };
  /** Next.js router for navigation */
  router: AppRouterInstance;
  /** Search metadata store for persisting search data */
  searchMetadataStore: SearchMetadataStoreType;
  /** Callback to update messages state */
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  /** Callback to set error state */
  setError: (error: string | null) => void;
  /** Thread ID for the current chat (used for cache fallback) */
  threadId?: string;
  /** Selected search context for the current user turn, if any */
  selectedSearchId?: string | null;
  /** Index of the assistant message in the messages array (for concurrent stream safety) */
  assistantMessageIndex: number;
  /** OpenTelemetry run ID for trace linking (mutable, set by metadata event) */
  runId?: string;
  /** LangSmith project name for trace URL construction (mutable, set by metadata event) */
  langsmithProject?: string;
  /** LangSmith workspace ID for trace URL construction (mutable, set by metadata event) */
  langsmithWorkspaceId?: string;
  /** LangSmith project ID (UUID) for trace URL construction (mutable, set by metadata event) */
  langsmithProjectId?: string;
  /** First discovered search ID for this stream (used to keep thread metadata in sync) */
  primarySearchId?: string;
}

// Re-export ChatMessage for consumers that import from this module
export type { ChatMessage };

/**
 * Handles metadata events (stream start).
 * Stores the run_id and LangSmith info in context for trace linking.
 */
export function handleMetadataEvent(
  event: ShopAgentMetadataEvent,
  context: StreamHandlerContext
): EventHandlerResult {
  // Store run_id and LangSmith info in context for inclusion in messages
  context.runId = event.run_id;
  context.langsmithProject = event.langsmith_project ?? undefined;
  context.langsmithWorkspaceId = event.langsmith_workspace_id ?? undefined;
  context.langsmithProjectId = event.langsmith_project_id ?? undefined;

  // Cache LangSmith fields by run_id AND thread_id so they persist across navigation
  // This allows TraceLink to look up fields even after history reload (when run_id is lost)
  if (event.run_id) {
    cacheLangSmithFields(event.run_id, context.threadId, {
      langsmithProject: event.langsmith_project ?? undefined,
      langsmithWorkspaceId: event.langsmith_workspace_id ?? undefined,
      langsmithProjectId: event.langsmith_project_id ?? undefined,
    });
  }

  // Update the current assistant message with the run_id and LangSmith info
  context.setMessages((prev) => {
    if (prev.length === 0) return prev;
    const updated = [...prev];
    const targetIndex = context.assistantMessageIndex;
    const targetMessage = updated[targetIndex];
    if (targetMessage?.role === "assistant") {
      updated[targetIndex] = {
        ...targetMessage,
        runId: event.run_id,
        langsmithProject: event.langsmith_project ?? undefined,
        langsmithWorkspaceId: event.langsmith_workspace_id ?? undefined,
        langsmithProjectId: event.langsmith_project_id ?? undefined,
      };
    }
    return updated;
  });

  return "continue";
}

type SearchMetadataOptions = NonNullable<
  Parameters<SearchMetadataStoreType["setSearchMetadata"]>[3]
>;

interface SearchMetadataFromQuery {
  text: string;
  options: SearchMetadataOptions;
}

/**
 * Reads the `search` event's `query` dict. Since OCT-4616 it carries only
 * `search_id`, `text`, `limit` and `browse_menu_uuid`: the search itself is
 * the event's `intent`, and what ran is the cached page's `effectiveQuery`.
 */
function searchMetadataFromQuery(query: Record<string, unknown>): SearchMetadataFromQuery {
  const options: SearchMetadataOptions = {};
  if (query.browse_menu_uuid === null || typeof query.browse_menu_uuid === "string") {
    options.browseMenuUuid = query.browse_menu_uuid || null;
  }
  return { text: typeof query.text === "string" ? query.text : "", options };
}

/**
 * Handles a `search` event: one search the chat agent generated (OCT-4436).
 * Stores its metadata for the results page, adds it to the message's search
 * tools and opens the stream's first search.
 */
export function handleSearchEvent(
  event: ShopAgentSearchEvent,
  context: StreamHandlerContext
): EventHandlerResult {
  const searchId = event.search_id;
  if (!searchId) return "continue";
  // The tool filed this search in history: a preview that missed it before now asks again.
  forgetStoredSearchMiss(searchId);
  const { text, options } = searchMetadataFromQuery(event.query ?? {});
  // The results page runs the search as this `/v1` request.
  options.intent = event.intent?.q
    ? {
        q: event.intent.q,
        facets: (event.intent.facets ?? []).map((facet) => ({
          name: facet.name,
          values: [...facet.values],
        })),
      }
    : null;

  // Store search metadata by searchId
  try {
    context.searchMetadataStore.setSearchMetadata(searchId, text, [], options);

    // Capture the first discovered search as the primary search for this stream.
    // We then keep threadId-keyed metadata synced with that primary search so
    // initial /results/{threadId}?agent=true loads don't drift from searchId-keyed
    // metadata that can be updated by later search events.
    if (!context.primarySearchId) {
      context.primarySearchId = searchId;
    }

    // IMPORTANT: Also store by threadId for the primary search.
    // This enables useResultsPageSearch to subscribe using the threadId before
    // the searchId is known. The subscription callback will receive the metadata
    // and execute the search. This creates intentional coupling between this handler
    // and useResultsPageSearch - both must agree on using threadId as the key for
    // the initial search metadata lookup.
    // We pass originalSearchId to preserve the AI-generated searchId in the metadata
    // even when using threadId as the storage key.
    if (context.threadId && searchId === context.primarySearchId) {
      context.searchMetadataStore.setSearchMetadata(context.threadId, text, [], {
        ...options,
        originalSearchId: searchId,
      });
    }
  } catch (metadataError) {
    console.error("Failed to store search metadata:", metadataError);
  }

  // Navigate to the stream's first search if auto-navigate is enabled
  const isFirstSearch = context.searchTools.length === 0;
  context.searchTools.push({
    searchId,
    label: text,
    searchText: text,
  });
  if (isFirstSearch && context.shouldAutoNavigate.current) {
    context.shouldAutoNavigate.current = false;
    context.router.push(`/results/${searchId}`);
  }

  // Update messages with current search tools
  const currentSearchTools = context.searchTools.map((tool) => ({
    searchId: tool.searchId,
    label: tool.label,
    searchText: tool.searchText,
  }));

  context.setMessages((prev) => {
    if (prev.length === 0) return prev;
    const updated = [...prev];
    const targetIndex = context.assistantMessageIndex;
    const targetMessage = updated[targetIndex];
    // Only update assistant messages - user messages shouldn't have searchTools
    if (!targetMessage || targetMessage.role !== "assistant") return prev;
    updated[targetIndex] = {
      ...targetMessage,
      searchTools: currentSearchTools,
    };
    return updated;
  });

  return "continue";
}

/**
 * Processes a parsed message response and updates state.
 * Updates the final message content, search tool labels, and recommendations.
 */
function processMessageData(messageData: CosimoAgentResponse, context: StreamHandlerContext): void {
  const finalMessage = messageData.message || "";

  // Extract and collect brand results
  const extractedBrands = extractBrandResults(messageData);
  context.brandResults.push(...extractedBrands);

  context.setMessages((prev) => {
    if (prev.length === 0) return prev;
    const updated = [...prev];
    const targetIndex = context.assistantMessageIndex;
    const targetMessage = updated[targetIndex];

    // Guard against invalid index
    if (!targetMessage) return prev;

    // Update search tool labels
    const currentTools = targetMessage.searchTools || [];
    const updatedTools = updateSearchToolsWithLabels(currentTools, messageData);

    // Update recommendations in metadata store
    if (messageData.searches && Array.isArray(messageData.searches)) {
      messageData.searches.forEach((search: SearchWithRecommendations, index: number) => {
        const searchId = search.search_id;
        const recommendations = {
          brands: [],
          attributes: search.recommended_attributes || [],
          justification: search.justification,
        };

        if (searchId) {
          context.searchMetadataStore.updateRecommendations(searchId, recommendations);

          // Also update by threadId for the first search so subscriptions on threadId receive updates
          if (index === 0 && context.threadId) {
            context.searchMetadataStore.updateRecommendations(context.threadId, recommendations);
          }
        }
        if (searchId) {
          context.searchMetadataStore.markComplete(searchId);

          // Also mark complete by threadId for the first search
          if (index === 0 && context.threadId) {
            context.searchMetadataStore.markComplete(context.threadId);
          }
        }
      });
    }

    updated[targetIndex] = {
      role: "assistant",
      content: finalMessage,
      isStreaming: false,
      timestamp: context.assistantTimestamp,
      searchTools: updatedTools,
      brandResults: context.brandResults.length > 0 ? [...context.brandResults] : undefined,
      runId: context.runId,
      langsmithProject: context.langsmithProject,
      langsmithWorkspaceId: context.langsmithWorkspaceId,
      langsmithProjectId: context.langsmithProjectId,
    };
    return updated;
  });
}

/**
 * Handles message events (final AI response).
 * Parses the response content and updates the message state.
 */
export function handleMessageEvent(
  event: ShopAgentMessageEvent,
  context: StreamHandlerContext
): EventHandlerResult {
  if (!event.content) {
    return "continue";
  }

  // Parse the message content
  const messageData = parseCosimoAgentResponse(event.content);

  if (messageData) {
    processMessageData(messageData, context);
  } else {
    // Parsing failed - show error message
    console.error("[ChatStream] Failed to parse message content");
    context.setMessages((prev) => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      const targetIndex = context.assistantMessageIndex;
      const targetMessage = updated[targetIndex];

      // Guard against invalid index
      if (!targetMessage) return prev;

      const currentTools = (targetMessage.searchTools || []).map((tool) => ({
        searchId: tool.searchId,
        label: tool.label,
        searchText: tool.searchText,
      }));

      updated[targetIndex] = {
        role: "assistant",
        content: "Sorry, I encountered an error formatting the response.",
        isStreaming: false,
        timestamp: context.assistantTimestamp,
        searchTools: currentTools,
        runId: context.runId,
        langsmithProject: context.langsmithProject,
        langsmithWorkspaceId: context.langsmithWorkspaceId,
        langsmithProjectId: context.langsmithProjectId,
      };
      return updated;
    });
  }

  return "continue";
}

/**
 * Handles error events from the stream.
 * Sets the error state and updates the message to show an error.
 */
export function handleErrorEvent(
  event: ShopAgentErrorEvent,
  context: StreamHandlerContext
): EventHandlerResult {
  const errorMsg = event.message || "An error occurred";
  const statusCode = event.status_code;
  console.error(`[ChatStream] Stream error ${statusCode}: ${errorMsg}`);

  context.setError(errorMsg);

  context.setMessages((prev) => {
    if (prev.length === 0) return prev;
    const updated = [...prev];
    const targetIndex = context.assistantMessageIndex;
    const targetMessage = updated[targetIndex];

    // Guard against invalid index
    if (!targetMessage) return prev;

    const currentTools = (targetMessage.searchTools || []).map((tool) => ({
      searchId: tool.searchId,
      label: tool.label,
      searchText: tool.searchText,
    }));

    updated[targetIndex] = {
      role: "assistant",
      content: "Sorry, I encountered an error processing your request.",
      isStreaming: false,
      timestamp: context.assistantTimestamp,
      searchTools: currentTools,
      brandResults: context.brandResults.length > 0 ? [...context.brandResults] : undefined,
      runId: context.runId,
      langsmithProject: context.langsmithProject,
      langsmithWorkspaceId: context.langsmithWorkspaceId,
      langsmithProjectId: context.langsmithProjectId,
    };
    return updated;
  });

  return "break";
}

/**
 * Handles end events (stream complete).
 * Logs completion and signals to stop processing.
 */
export function handleEndEvent(): EventHandlerResult {
  console.debug("[ChatStream] Stream completed");
  return "break";
}
